// Send dates picked with a timezone are wall time in that timezone ("2026-10-22 09:00:00"),
// the API converts them to UTC the same way as the pop-up store life span.
// Values with an offset (ISO strings from toISOString) are exact moments.

const hasOffset = (value) => /(z|[+-]\d{2}:?\d{2})$/i.test(value)

const pad = (number) => String(number).padStart(2, '0')

const getZonedParts = (date, timezone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date)

  return parts.reduce((acc, { type, value }) => {
    if (type !== 'literal') acc[type] = Number(value)
    return acc
  }, {})
}

// "2026-10-22 09:00:00" for the API, built from the local fields of a picker date
export const formatWallTime = (date) => [
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
  `${pad(date.getHours())}:${pad(date.getMinutes())}:00`,
].join(' ')

// Date whose local fields show the value's wall time in the timezone (what the picker displays)
export const toWallTimeDate = (value, timezone) => {
  if (!hasOffset(value)) return new Date(value.replace(' ', 'T'))

  const { year, month, day, hour, minute, second } = getZonedParts(new Date(value), timezone)
  return new Date(year, month - 1, day, hour, minute, second)
}

// The exact moment the value means, used to compare it with now
export const toUtcDate = (value, timezone) => {
  if (hasOffset(value) || !timezone) return new Date(value.replace(' ', 'T'))

  const wallTime = toWallTimeDate(value, timezone)
  const wallTimeAsUtc = Date.UTC(
    wallTime.getFullYear(),
    wallTime.getMonth(),
    wallTime.getDate(),
    wallTime.getHours(),
    wallTime.getMinutes(),
    wallTime.getSeconds(),
  )
  const getOffset = (timestamp) => {
    const zoned = getZonedParts(new Date(timestamp), timezone)
    return Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second) - timestamp
  }
  // the offset at the guessed moment can differ on DST change days, so check it once more
  const offset = getOffset(wallTimeAsUtc)
  const correctedOffset = getOffset(wallTimeAsUtc - offset)

  return new Date(wallTimeAsUtc - correctedOffset)
}

const getTimezoneAbbreviation = (date, timezone) => new Intl.DateTimeFormat('en-US', {
  timeZone: timezone,
  timeZoneName: 'short',
}).formatToParts(date).find(({ type }) => type === 'timeZoneName')?.value ?? ''

// "Oct 22, 2026, 9:00 AM EDT"
export const formatSendDate = (value, timezone) => {
  const date = toUtcDate(value, timezone)
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)

  return `${formatted} ${getTimezoneAbbreviation(date, timezone)}`
}
